import React, { useEffect } from 'react';

import Router from '@/router';

import useNetwork from '@/hooks/useNetwork';

import { Layout, notification, ConfigProvider, theme, App as AntdApp } from 'antd';

import Navigation from '@/app/Navigation';

import { useDispatch, useSelector } from 'react-redux';
import { selectAuth, selectCurrentAdmin } from '@/redux/auth/selectors';
import { selectIsDarkMode } from '@/redux/theme';
import HeaderContent from '@/app/HeaderContent';
import { fetchRoles } from '@/redux/rolesPermissions';
import GlobalProfileAlert from '@/components/GlobalProfileAlert';
import FirstLoginWelcomeModal from '@/components/FirstLoginWelcomeModal';
import GlobalReminderModal from '@/components/Notification/GlobalReminderModal';
import { ThemeProvider } from '@/context/theme';
import { AttendanceProvider } from '@/context/AttendanceContext';
import '@/styles/darkModeNotifications.css';
import { setAntdInstances } from '@/utils/notificationHelper';
import ChatbotWidget from '@/components/ChatbotWidget';

import { designTokens } from '@/config/designTokens';

import TopNav from '@/components/Dashboard/TopNav';
import { useLocation } from 'react-router-dom';

// Paths that render without ERP chrome — no TopNav, no layout wrapper
const EMBED_PATHS = ['/seo-snippet', '/google-ads'];

const AntdGlobalSync = () => {
  const { message, notification, modal } = AntdApp.useApp();

  useEffect(() => {
    setAntdInstances(message, notification);
  }, [message, notification]);

  return null;
};

const LayoutWrapper = () => {
  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)' }}>
      <TopNav />
      <Layout.Content>
        <Router isLoggedIn={true} />
      </Layout.Content>
    </Layout>
  );
};

// Renders with or without TopNav depending on the current path
const EmbedAwareLayout = () => {
  const location = useLocation();
  const isEmbed = EMBED_PATHS.some(p => location.pathname.startsWith(p));

  if (isEmbed) {
    return <Router isLoggedIn={true} />;
  }

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)' }}>
      <TopNav />
      <Layout.Content>
        <Router isLoggedIn={true} />
      </Layout.Content>
    </Layout>
  );
};

function App() {
  const { isLoggedIn } = useSelector(selectAuth);
  const dispatch = useDispatch()
  const isDarkMode = useSelector(selectIsDarkMode);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode);
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    if (document.body) {
      document.body.classList.toggle('dark', isDarkMode);
      document.body.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    }
  }, [isDarkMode]);

  const themeConfig = {
    algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: designTokens.colors.primary,
      colorSuccess: designTokens.colors.success,
      colorWarning: designTokens.colors.warning,
      colorError: designTokens.colors.error,
      colorInfo: designTokens.colors.secondary,
      fontFamily: designTokens.typography.fontFamily,
      fontSize: designTokens.typography.fontSizeBase,
      fontSizeSM: parseInt(designTokens.typography.fontSize.sm),
      fontSizeLG: parseInt(designTokens.typography.fontSize.lg),
      fontSizeXL: parseInt(designTokens.typography.fontSize.xl),
      fontSizeHeading1: parseInt(designTokens.typography.fontSize['3xl']),
      fontSizeHeading2: parseInt(designTokens.typography.fontSize['2xl']),
      fontSizeHeading3: parseInt(designTokens.typography.fontSize.xl),
      fontSizeHeading4: parseInt(designTokens.typography.fontSize.lg),
      fontSizeHeading5: parseInt(designTokens.typography.fontSize.md),
      borderRadius: parseInt(designTokens.spacing.radius),

      // Background and Text sync
      colorBgBase: isDarkMode ? designTokens.colors.background.dark : designTokens.colors.background.light,
      colorTextBase: isDarkMode ? designTokens.colors.text.dark : designTokens.colors.text.light,
    },
    components: {
      Layout: {
        colorBgHeader: isDarkMode ? designTokens.colors.background.dark : '#ffffff',
        colorBgBody: isDarkMode ? designTokens.colors.background.dark : designTokens.colors.background.light,
      },
      Card: {
        borderRadiusLG: parseInt(designTokens.spacing.radius),
        colorBgContainer: isDarkMode ? designTokens.colors.card.dark : designTokens.colors.card.light,
      },
      Table: {
        borderRadius: parseInt(designTokens.spacing.radius),
        colorBgContainer: isDarkMode ? designTokens.colors.card.dark : designTokens.colors.card.light,
        fontSize: designTokens.typography.fontSizeBase,
      },
      Form: {
        labelFontSize: designTokens.typography.fontSizeBase,
      },
      Input: {
        fontSize: designTokens.typography.fontSizeBase,
      },
      Button: {
        borderRadius: parseInt(designTokens.spacing.radius),
        borderRadiusSM: parseInt(designTokens.spacing.radius) / 1.5,
        fontWeight: designTokens.typography.fontWeight.bold,
      },
      Notification: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? '#ffffff' : '#000000',
        borderRadius: 8,
      },
      Modal: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
        colorTextHeading: isDarkMode ? '#ffffff' : 'rgba(0, 0, 0, 0.88)',
      },
      Drawer: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
        colorText: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
      },
      Tag: {
        colorBgContainer: isDarkMode ? '#262626' : '#fafafa',
        colorText: isDarkMode ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.88)',
      },
      Popover: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
      },
      Select: {
        colorBgElevated: isDarkMode ? '#1f1f1f' : '#ffffff',
      },
    },
  };

  if (!isLoggedIn) return (
    <ThemeProvider>
      <AttendanceProvider>
        <ConfigProvider theme={themeConfig}>
          <AntdApp
            message={{ duration: 3, maxCount: 3, top: 80 }}
            notification={{ duration: 4.5, maxCount: 3, top: 80, placement: 'topRight' }}
          >
            <AntdGlobalSync />
            <Router />
          </AntdApp>
        </ConfigProvider>
      </AttendanceProvider>
    </ThemeProvider>
  );

  else {
    dispatch(fetchRoles())
    return (
      <ThemeProvider>
        <AttendanceProvider>
          <ConfigProvider theme={themeConfig}>
            <AntdApp
              message={{ duration: 3, maxCount: 3, top: 80 }}
              notification={{ duration: 4.5, maxCount: 3, top: 80, placement: 'topRight' }}
            >
              <AntdGlobalSync />
              <GlobalProfileAlert />
              <FirstLoginWelcomeModal />
              <GlobalReminderModal />
              <ChatbotWidget />
              <EmbedAwareLayout />
            </AntdApp>
          </ConfigProvider>
        </AttendanceProvider>
      </ThemeProvider>
    );
  }
}

export default App;